import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    EmailField,
    BooleanField,
    DateField,
    FunctionField,
    EditButton,
    Filter,
    TextInput,
    BooleanInput,
    ReferenceInput,
    SelectInput,
    TopToolbar,
    CreateButton,
    ExportButton,
    RefreshButton,
    useTranslate,
    useListContext,
    usePermissions,
    RecordContextProvider,
} from 'react-admin';
import { Chip, Box, Typography, Fab, useMediaQuery, useTheme } from '@mui/material';
import { CheckCircle, Cancel, Business, Add as AddIcon, Person } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const UserFilter = (props: any) => {
    const translate = useTranslate();

    return (
        <Filter {...props}>
            <TextInput
                label={translate('resources.backend-users.list.filters.search')}
                source="q"
                alwaysOn
            />
            <ReferenceInput
                label={translate('resources.backend-users.fields.roleId')}
                source="roleId"
                reference="roles"
            >
                <SelectInput optionText="name" />
            </ReferenceInput>
            <BooleanInput
                label={translate('resources.backend-users.list.filters.activeOnly')}
                source="isActive"
            />
            <SelectInput
                source="language"
                label={translate('resources.backend-users.fields.language')}
                choices={[
                    { id: 'en', name: translate('language.en') },
                    { id: 'zh-TW', name: translate('language.zh-TW') },
                ]}
            />
        </Filter>
    );
};

const DesktopListActions = ({ canCreate }: { canCreate: boolean }) => (
    <TopToolbar>
        {canCreate && <CreateButton />}
        <ExportButton />
        <RefreshButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0 }}>
        <ExportButton />
        <RefreshButton />
    </Box>
);

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    const translate = useTranslate();
    if (isLoading || !data) return null;
    return (
        <Box>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box
                        onClick={() => navigate(`/backend-users/${record.id}`)}
                        sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                <Person sx={{ fontSize: 16, color: '#666', flexShrink: 0 }} />
                                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {record.username}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                <Chip
                                    icon={record.isActive ? <CheckCircle /> : <Cancel />}
                                    label={record.isActive ? translate('resources.backend-users.status.active') : translate('resources.backend-users.status.inactive')}
                                    color={record.isActive ? 'success' : 'default'}
                                    size="small"
                                    sx={{ height: 22, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: '0.85rem' } }}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '40%' }}>
                                {record.email}
                            </Typography>
                            {record.roleName && (
                                <Chip label={record.roleName} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                            )}
                            {record.vendors && record.vendors.length > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Business sx={{ fontSize: 14, color: '#999' }} />
                                    <Typography variant="caption" color="text.secondary">
                                        {record.vendors[0].vendorName}
                                    </Typography>
                                </Box>
                            )}
                            <Chip
                                label={translate(`language.${record.language}`)}
                                size="small"
                                sx={{ height: 20, fontSize: '0.6rem', backgroundColor: '#E3F2FD', color: '#1976D2' }}
                            />
                        </Box>
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

export const UserList = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const { permissions } = usePermissions();
    const perms = (permissions as string[] | null | undefined) || [];
    const canCreate = perms.includes('users:create:all') || perms.includes('users:write:all') || perms.includes('*:*:*');
    const canEdit = perms.includes('users:update:all') || perms.includes('users:write:all') || perms.includes('*:*:*');

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                filters={<UserFilter />}
                actions={isMobile ? <MobileListActions /> : <DesktopListActions canCreate={canCreate} />}
                empty={isMobile ? false : undefined}
                perPage={25}
                sort={{ field: 'createdAt', order: 'DESC' }}
                sx={{
                    '& .RaList-content': {
                        backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                    },
                    ...(isMobile && {
                        '& .RaList-main': { margin: 0 },
                        '& .RaList-content': { backgroundColor: 'transparent !important', boxShadow: 'none !important', mt: 2 },
                        '& .RaList-actions': { backgroundColor: 'transparent !important', p: '0 !important', minHeight: 'auto !important' },
                        '& .RaListToolbar-root': { backgroundColor: 'transparent !important', flexDirection: 'row !important', flexWrap: 'wrap !important', alignItems: 'center !important', gap: '8px !important' },
                        '& .RaListToolbar-root > span': { display: 'none' },
                        '& .RaFilterButton-root, & .add-filter': { display: 'none !important' },
                        '& .RaListToolbar-root > form, & .RaFilterForm-root': { flex: '1 1 100% !important', width: '100% !important' },
                        '& .filter-field, & .RaFilterForm-filterFormInput': { width: '100% !important' },
                        '& .filter-field .MuiFormControl-root': { width: '100% !important' },
                        '& .filter-field .MuiInputBase-root': { width: '100% !important' },
                        '& .MuiToolbar-root': { backgroundColor: 'transparent !important', minHeight: 'auto !important' },
                    }),
                }}
            >
                {isMobile ? (
                    <MobileList />
                ) : (
                    <Datagrid
                        bulkActionButtons={false}
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 600,
                            },
                            '& .RaDatagrid-row': {
                                '&:hover': {
                                    backgroundColor: '#F0F7FF',
                                },
                            },
                        }}
                    >
                        <TextField source="username" />
                        <EmailField source="email" />

                        <TextField source="roleName" emptyText="-" />

                        <BooleanField
                            source="isActive"
                            valueLabelTrue={translate('resources.backend-users.status.active')}
                            valueLabelFalse={translate('resources.backend-users.status.inactive')}
                            TrueIcon={CheckCircle}
                            FalseIcon={Cancel}
                            sx={{
                                '& .RaBooleanField-true': {
                                    color: '#00C853',
                                },
                                '& .RaBooleanField-false': {
                                    color: '#F44336',
                                },
                            }}
                        />

                        <BooleanField
                            source="totpEnabled"
                            TrueIcon={CheckCircle}
                            FalseIcon={Cancel}
                            sx={{
                                '& .RaBooleanField-true': {
                                    color: '#00C853',
                                },
                                '& .RaBooleanField-false': {
                                    color: '#909090',
                                },
                            }}
                        />

                        <FunctionField
                            source="language"
                            render={(record: any) => (
                                <Chip
                                    label={translate(`language.${record.language}`)}
                                    size="small"
                                    sx={{
                                        backgroundColor: '#E3F2FD',
                                        color: '#1976D2',
                                        fontWeight: 500,
                                    }}
                                />
                            )}
                        />

                        <FunctionField
                            label={translate('resources.backend-users.fields.vendor')}
                            render={(record: any) => {
                                if (!record.vendors || record.vendors.length === 0) {
                                    return (
                                        <Typography variant="body2" color="text.secondary">
                                            {translate('resources.backend-users.noVendor')}
                                        </Typography>
                                    );
                                }
                                const vendor = record.vendors[0];
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Business sx={{ fontSize: 16, color: '#666' }} />
                                        <Typography variant="body2">
                                            {vendor.vendorName}
                                        </Typography>
                                        <Chip
                                            label={vendor.role}
                                            size="small"
                                            sx={{
                                                ml: 0.5,
                                                height: 20,
                                                fontSize: '0.7rem',
                                                backgroundColor: '#F0F0F0',
                                            }}
                                        />
                                    </Box>
                                );
                            }}
                        />

                        <DateField
                            source="lastLoginAt"
                            showTime
                            options={{
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            }}
                        />

                        <DateField
                            source="createdAt"
                            options={{
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                            }}
                        />

                        {canEdit && <EditButton />}
                    </Datagrid>
                )}
            </List>
            {isMobile && canCreate && (
                <Fab
                    color="primary"
                    onClick={() => navigate('/backend-users/create')}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        backgroundColor: '#009A75',
                        '&:hover': { backgroundColor: '#007A5E' },
                        zIndex: 1100,
                    }}
                >
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};
